import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Heart, Sparkles, CheckCircle2, DollarSign, Award, Leaf, Tag } from "lucide-react";
import Brand from "../components/Brand";

/* /poofy — public sales page for Mike's Poofy Organics affiliate.
   No login required. Every CTA goes to https://poofyorganics.com/?ref=faitharama. */

const POOFY_URL      = "https://poofyorganics.com/?ref=faitharama";
const POOFY_PRODUCTS = "/affiliates/poofy-products.png";
const POOFY_LOGO     = "/affiliates/poofy-logo.png";

const GREEN = "#2E7D32";
const LIME  = "#7DBE3D";
const GOLD  = "#E8B539";
const NAVY = "#0F172A";
const CREAM = "#FFFFFF";
const WINE  = "#0F172A";

const TIERS = [
  { range: "$1 – —",        reward: "$25 product credit",  pop: "Starter month" },
  { range: "$500 – —",      reward: "— cash bonus",      pop: "One sale a week" },
  { range: "$1,000 – $1,999",  reward: "— cash bonus",     pop: "Picking up steam", highlight: true },
  { range: "$2,000 – $2,999",  reward: "— cash bonus",     pop: "Going pro" },
  { range: "$3,000 – $4,999",  reward: "— cash bonus",     pop: "Whole-family income" },
  { range: "$5,000+",          reward: "— cash bonus",     pop: "Top earner",        highlight: true },
];

const WHY = [
  { Icon: Leaf,       title: "Toxin-Free, Period",     text: "No parabens, no phthalates, no fake fragrance. The ingredients list reads like a kitchen pantry." },
  { Icon: ShieldCheck,title: "USDA Certified Organic", text: "Real organic, real ingredients — vetted by USDA, not a marketing sticker." },
  { Icon: Heart,      title: "Made In The USA",        text: "Hand-blended in small batches by a family business — quality you can actually trace." },
  { Icon: Award,      title: "Not On Amazon",          text: "You won't find Poofy on Amazon. Only members can sell it — that's why your link actually matters. No shopping around for a cheaper version, no big-box undercut." },
  { Icon: Sparkles,   title: "Whole-Family Lineup",    text: "Skincare, body, baby, oral, home cleaners — switch a few products and you've cleaned up your whole house." },
];

export default function PoofyPage() {
  return (
    <div className="min-h-screen" style={{ background: CREAM }}>
      <SiteNav />

      {/* ═══ HERO ═══ */}
      <section className="relative overflow-hidden"
               style={{ background: `linear-gradient(180deg, ${CREAM} 0%, #F0F8E8 50%, #E8F5DD 100%)` }}>
        <div className="relative max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-16">
          <Link to="/market" className="text-[11px] tracking-[0.3em] uppercase font-bold hover:opacity-70 transition"
                style={{ color: GREEN }} data-testid="poofy-back-btn">
            ← Back To Market
          </Link>

          <div className="grid md:grid-cols-[1fr_440px] gap-8 md:gap-10 items-center mt-5">
            {/* Left — copy */}
            <div>
              <div className="flex items-center gap-3 flex-wrap mb-4">
                <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: GREEN }}>
                  ★ <Brand /> · WELLNESS PARTNER ★
                </div>
                <span className="text-[10px] tracking-[0.18em] uppercase font-bold px-2.5 py-1 rounded-full text-white"
                      style={{ background: GREEN }}>USDA CERTIFIED ORGANIC</span>
              </div>

              <div className="inline-block bg-white rounded-2xl px-6 py-3 shadow-md mb-5"
                   style={{ border: `3px solid ${GREEN}` }}>
                <img src={POOFY_LOGO} alt="Poofy Organics" className="block h-12 md:h-14 w-auto" />
              </div>

              <h1 className="text-5xl md:text-6xl uppercase leading-[0.95]"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, color: WINE, textWrap: "balance" }}>
                Toxin-Free<br/>Living<span style={{ color: GREEN }}>.</span>
              </h1>

              <p className="mt-4 text-lg italic font-semibold" style={{ color: WINE }}>
                If you don't want you or your family comin' in contact with chemicals — this is your answer.
              </p>
              <p className="mt-2 text-base" style={{ color: "#3D3D3D" }}>
                Real USDA-certified organic ingredients. Hand-blended in the USA by a family business. Skincare, body, baby, oral, home cleaners — switch a few products and you've cleaned up your whole house.
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <a href={POOFY_URL} target="_blank" rel="noopener noreferrer"
                   data-testid="poofy-shop-cta"
                   className="inline-flex items-center gap-2 px-7 py-4 rounded-full text-base font-bold uppercase tracking-wider shadow-lg hover:scale-[1.03] transition"
                   style={{ background: GREEN, color: "#fff", border: `2px solid ${NAVY}` }}>
                  Shop Poofy Organics <ArrowRight className="w-4 h-4" />
                </a>
                <a href="#earn"
                   data-testid="poofy-earn-link"
                   className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-bold uppercase tracking-wider border-2 hover:bg-white transition"
                   style={{ borderColor: NAVY, color: NAVY }}>
                  How You Earn <DollarSign className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Right — product lineup photo */}
            <div className="rounded-2xl overflow-hidden shadow-2xl border-4 bg-white"
                 style={{ borderColor: GREEN }}>
              <img src={POOFY_PRODUCTS} alt="Poofy Organics product lineup — soaps, sprays, cleaners, candles, oils"
                   className="block w-full h-auto"
                   data-testid="poofy-hero-image" />
            </div>
          </div>
        </div>
      </section>

      {/* ═══ WHY POOFY ═══ */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-16">
        <div className="text-center mb-10">
          <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-2" style={{ color: GOLD }}>
            ★ WHY POOFY ★
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl uppercase leading-[1.05]" style={{ color: NAVY }}>
            Real Ingredients. Real Results.
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {WHY.map((b, i) => (
            <div key={i} data-testid={`poofy-why-${i}`}
                 className="rounded-xl border-2 bg-white p-5 flex gap-4 shadow-sm hover:shadow-md transition"
                 style={{ borderColor: "#BAE6FD" }}>
              <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                   style={{ background: GREEN, color: "#fff" }}>
                <b.Icon className="w-6 h-6" />
              </div>
              <div>
                <div className="font-bold text-lg leading-tight" style={{ color: NAVY }}>{b.title}</div>
                <div className="text-sm mt-1 leading-snug" style={{ color: "#3D3D3D" }}>{b.text}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ EARN — Commission Tiers ═══ */}
      <section id="earn" className="py-12 md:py-16"
               style={{ background: `linear-gradient(180deg, #FFFFFF 0%, ${CREAM} 100%)` }}>
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="text-center mb-8 max-w-3xl mx-auto">
            <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-2" style={{ color: WINE }}>
              ★ MEMBER OPPORTUNITY ★
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl uppercase leading-[1.05]" style={{ color: NAVY }}>
              15% On Every Sale.<br/>Plus Monthly Bonuses.
            </h2>
            <p className="mt-4 text-base italic" style={{ color: "#3D3D3D" }}>
              Sign up as a Poofy affiliate, share your link, and earn on every product —
              every time. Future orders from your customers still pay you, even if they
              order direct. <strong style={{ color: WINE }}>And since Poofy isn't on Amazon,
              your link is the only way folks can buy it.</strong> No big-box undercut.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {TIERS.map((t, i) => (
              <div key={i} data-testid={`poofy-tier-${i}`}
                   className="rounded-xl border-2 bg-white p-5 shadow-sm hover:-translate-y-0.5 hover:shadow-lg transition"
                   style={{ borderColor: t.highlight ? GOLD : "#E5E1D6" }}>
                {t.highlight && (
                  <div className="inline-block text-[9px] tracking-[0.22em] uppercase font-bold px-2 py-0.5 rounded-full mb-2"
                       style={{ background: GOLD, color: NAVY }}>
                    ★ Sweet Spot
                  </div>
                )}
                <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: GREEN }}>
                  {t.pop}
                </div>
                <div className="font-bold text-xl" style={{ color: NAVY }}>{t.range}</div>
                <div className="text-2xl font-serif-display mt-1" style={{ color: WINE, fontWeight: 700 }}>
                  → {t.reward}
                </div>
              </div>
            ))}
          </div>

          <div className="text-[11px] mt-4 text-center italic" style={{ color: "#7A7066" }}>
            Bonuses aren't stackable — you earn the highest one for the month. Hit $1,000 in 6 months and unlock a 15% personal discount code too.
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a href={POOFY_URL} target="_blank" rel="noopener noreferrer"
               data-testid="poofy-affiliate-cta"
               className="inline-flex items-center gap-2 px-7 py-4 rounded-full text-base font-bold uppercase tracking-wider shadow-lg hover:scale-[1.03] transition"
               style={{ background: GOLD, color: NAVY, border: `2px solid ${NAVY}` }}>
              Become A Poofy Affiliate <Award className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
